import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Hoc from "./hoc/hoc";
import CircularProgress from '@mui/material/CircularProgress';

const PAGE_TITLES = {
  '/login': 'Login',
  '/logout': 'Logged Out',
  '/signup': 'Register',
  '/dashboard': 'Dashboard',
  '/radarchart': 'Radar Chart',
  '/myteam': 'My Team',
  '/mysystems': 'My Assets',
  '/settings': 'Settings',
  '/approve-teammates': 'Approve Teammates',
  '/forgot-pwd': 'Forgot Password',
  '/reset-pwd': 'Reset Password',
  '/assign': 'Assign',
  '/reassign': 'Reassign',
  '/terms-condition': 'Terms & Condition',
  '/privacy-policy': 'Privacy Policy',
};

const PageTitleSetter = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const title = PAGE_TITLES[pathname] || 'Client Access';
    document.title = `${title} - Client Access`;
  }, [pathname]);
  return null;
};

// Auth pages - loaded eagerly (needed immediately)
import Login from "./containers/CA2Login";
import LoginVerifyOTP from "./containers/LoginVerifyOTP";
import Logout from "./containers/LogoutSuccess";

// Lazy loaded pages - loaded on demand
const RegisterComplete = lazy(() => import("./containers/Register-complete"));
const ForgotPwd = lazy(() => import("./containers/ForgotPwd"));
const ResetPassword = lazy(() => import("./containers/Reset-password"));
const Register = lazy(() => import("./containers/Register"));
const ApproveTeamMates = lazy(() => import("./containers/ApproveTeamMates"));
const MyAssets = lazy(() => import("./containers/MyAssets"));
const Dashboard = lazy(() => import("./containers/Dashboard"));
const RadarChart = lazy(() => import("./containers/RadarChart"));
const MyTeam = lazy(() => import("./containers/MyTeam"));
const SelectRegion = lazy(() => import("./containers/SelectRegion"));
const SelectSite = lazy(() => import("./containers/SelectSite"));
const Reassign = lazy(() => import("./containers/Reassign"));
const signupSuccess = lazy(() => import("./containers/signup-success"));
const ReassignSublocation = lazy(() => import("./containers/ReassignSublocation"));
const VerifyOTP = lazy(() => import("./containers/VerifyOTP"));
const UserSettings = lazy(() => import("./containers/UserSettings"));
const Assign = lazy(() => import("./containers/Assign"));
const AssignSublocation = lazy(() => import("./containers/AssignSublocation"));
const TermsCondition = lazy(() => import("./containers/TermsCondition"));
const PrivacyPolicy = lazy(() => import("./containers/PrivacyPolicy"));

const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <CircularProgress size={42} style={{ color: '#F7931E' }} />
  </div>
);

const BaseRouter = () => (
  <Hoc>
    <PageTitleSetter />
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/login" Component={Login} />
        <Route path="/log/verify-otp" Component={LoginVerifyOTP} />
        <Route path="/logout" Component={Logout} />
        <Route path="/signup" exact Component={Register} />
        <Route path="/register-complete" Component={RegisterComplete} />
        <Route path="/forgot-pwd" Component={ForgotPwd} />
        <Route path="/reset-pwd" Component={ResetPassword} />
        <Route path="/approve-teammates" Component={ApproveTeamMates} />
        <Route path="/mysystems" Component={MyAssets} />
        <Route path="/radarchart" Component={RadarChart} />
        <Route path="/myteam" Component={MyTeam} />
        <Route path="/dashboard" Component={Dashboard} />
        <Route path="/select-region" Component={SelectRegion} />
        <Route path="/select-site" Component={SelectSite} />
        <Route path="/reassign" Component={Reassign} />
        <Route path="/settings" Component={UserSettings} />
        <Route path="/reassign-sublocation" Component={ReassignSublocation} />
        <Route path="/signup-success" Component={signupSuccess} />
        <Route path="/verify-otp" Component={VerifyOTP} />
        <Route path="/assign" Component={Assign} />
        <Route path="/assign-sublocation" Component={AssignSublocation} />
        <Route path="/terms-condition" Component={TermsCondition} />
        <Route path="/privacy-policy" Component={PrivacyPolicy} />

        <Route path="/" exact Component={Login} />
      </Routes>
    </Suspense>
  </Hoc>
);

export default BaseRouter;
